// API da feature Roteiro — lê e grava days do roteiro de uma viagem.
//
// Storage: já está 100% em Firestore (não usa mais Cloudflare KV). Os
// dados ficam em três blobs por trip:
//   trips/{tripId}/timeline/_legacy  → { days: [...], _ts }
//     - days[]: array com routes/cities/title/icon de cada dia,
//       identificado pela string `date` ("12/04"). NÃO tem id estável.
//       day.hotel é VIEW DERIVADA do blob hotels (canônico) — ainda
//       persistido aqui pra compat com leitores externos e reorder.
//   trips/{tripId}/itinerary/_legacy → { overrides: {[date]: {...}}, _ts }
//     - overrides[date]: { dayTitle?, dayIcon?, color?, items?: [...] }
//       Sobrescreve campos do day correspondente. items[] são os steps
//       do dia (time + emoji + title + details). Items podem ter
//       `linkedHotelId` / `linkedRouteId` apontando pra entidade
//       canônica (cascade ao deletar a entidade).
//   trips/{tripId}/hotels/_legacy    → { items: [...], _ts }
//     - items[]: hotéis canônicos com id estável + range checkin/checkout.
//       Um hotel cobre N dias (checkin <= day.date < checkout) — a view
//       em day.hotel é REPLICADA na hora do load, mas a fonte de verdade
//       é esse blob. Mutations escrevem aqui PRIMEIRO, depois re-derivam
//       o timeline.
//
// Por que blob único e não um doc por dia?
//   - O legacy já guarda assim e foi preservado na migração KV→Firestore
//     (ver src/core/cloudSync.js).
//   - Trips têm no máximo ~30 dias; payload total é pequeno (KB). Save full
//     vs save granular não impacta na escala atual.
//   - Saves sempre partem do estado completo (ver `replaceDays` /
//     `setDayOverride`) — isso é deliberado pra evitar drift.
//   - Quando precisar de queries por dia (filtros, paginação), aí refatora
//     pra docs individuais. Por enquanto, blob é suficiente.

import { cloudLoad, cloudSave } from '../../core/cloudSync.js';

/**
 * Carrega os dias do roteiro com overrides já aplicados.
 *
 * Auto-gera dias quando timeline está vazia E o trip tem `startDate`+`endDate`
 * (case da viagem nova): cria N dias com `date` real (DD/MM) e `title` "Dia N",
 * persiste no Firestore antes de devolver. Sem datas → devolve days vazio
 * (a UI mostra empty state com botão "Adicionar dia").
 *
 * Returns: { days: [...], rawTimeline, rawItinerary }
 *   - days[]: dias mergeados (timeline + itinerary overrides)
 *     Cada day:
 *       { date, icon, title, details, color, hotel, routes, cities, items }
 *     Note: `items` vem do itinerary.overrides[date].items (vazio se não há)
 *   - rawTimeline / rawItinerary: shape cru (pra updates depois)
 */
export async function loadRoteiro(tripId, trip = null) {
  const [timelineBlob, itineraryBlob, hotelsBlob] = await Promise.all([
    cloudLoad(tripId, 'timeline').catch((err) => {
      console.warn('[roteiro] cloudLoad timeline failed:', err);
      return {};
    }),
    cloudLoad(tripId, 'itinerary').catch((err) => {
      console.warn('[roteiro] cloudLoad itinerary failed:', err);
      return {};
    }),
    cloudLoad(tripId, 'hotels').catch((err) => {
      console.warn('[roteiro] cloudLoad hotels failed:', err);
      return {};
    }),
  ]);

  let rawTimeline = timelineBlob || {};
  const rawItinerary = itineraryBlob || {};
  let rawHotels = hotelsBlob || {};
  let timelineDays = rawTimeline.days || [];

  // Auto-gen lazy: timeline vazia + trip com datas → gera "Dia 1..N" e persiste.
  // Side effect deliberado: primeiro load de uma viagem com datas grava o
  // skeleton no Firestore, então das próximas vezes já vem pronto. Sem datas,
  // ou se a timeline já tem dias, não toca.
  if (timelineDays.length === 0 && trip?.startDate && trip?.endDate) {
    const generated = generateBlankDaysFromTripDates(trip);
    if (generated.length) {
      timelineDays = generated;
      rawTimeline = { ...rawTimeline, days: generated, _ts: Date.now() };
      try {
        await cloudSave(tripId, 'timeline', rawTimeline);
      } catch (err) {
        console.warn('[roteiro] auto-gen save failed:', err);
      }
    }
  }

  // Migração on-first-read: se o blob `hotels` está vazio MAS a timeline
  // tem days[].hotel, deriva os hotéis canônicos da timeline e persiste.
  // Idempotente — se canônico já tem items, não migra de novo.
  let hotelsItems = Array.isArray(rawHotels.items) ? rawHotels.items : [];
  if (hotelsItems.length === 0) {
    const legacyHas = timelineDays.some((d) => d?.hotel?.name);
    if (legacyHas) {
      hotelsItems = deriveHotelsFromTimelineDays(timelineDays);
      if (hotelsItems.length) {
        rawHotels = { ...rawHotels, items: hotelsItems, _ts: Date.now() };
        try {
          await cloudSave(tripId, 'hotels', rawHotels);
        } catch (err) {
          console.warn('[roteiro] hotels migration save failed:', err);
        }
      }
    }
  }

  // Heal duplicate routes: rotas multi-day (voo 11/08 → 12/08 etc) eram
  // replicadas no timeline legado via `_replicateRoutesInTimeline` — cada
  // dia do range ganhava UMA cópia da rota. Edits via updateDayRoute (que
  // varre todos os days) propagam pra todas as cópias hoje, MAS dados
  // gravados antes da propagação podem ter cópias incompletas (dia 11 cheio,
  // dia 12 quase vazio).
  // Heal: pra cada route.id que aparece em múltiplos days, mescla os campos
  // (prioriza valores não-vazios) e reescreve em todas as cópias. Idempotente.
  const healed = healDuplicateRoutes(timelineDays);
  if (healed.changed) {
    timelineDays = healed.days;
    rawTimeline = { ...rawTimeline, days: timelineDays, _ts: Date.now() };
    try {
      await cloudSave(tripId, 'timeline', rawTimeline);
    } catch (err) {
      console.warn('[roteiro] routes heal save failed:', err);
    }
  }

  // Derive view: cada day ganha day.hotel via lookup no canônico. Também
  // re-injetamos no timelineDays pra mergeDays já produzir o shape final
  // que renderers consomem (e o reorder continua funcionando com day.hotel
  // presente — reconcileHotelsAfterReorder vira backup defensivo).
  const derivedDays = timelineDays.map((d) => {
    const hotel = lookupHotelForDay(hotelsItems, d.date);
    // Cópia rasa pra não compartilhar refs entre days (renderers podem
    // mutar local sem afetar outro day).
    return { ...d, hotel: hotel ? { ...hotel } : null };
  });

  const days = mergeDays(derivedDays, rawItinerary.overrides || {});

  return { days, rawTimeline, rawItinerary, rawHotels };
}

/**
 * Heal duplicate routes: rotas multi-day apareciam replicadas em todos os
 * days do range (legacy `_replicateRoutesInTimeline`). Edits via
 * `updateDayRoute` hoje varrem todas as cópias, mas dados PRÉ-existentes
 * podem ter cópias incompletas (ex.: voo 11/08→12/08 com dados completos
 * no dia 11 e quase vazios no dia 12).
 *
 * Esse helper roda uma única vez (idempotente) on load: pra cada `route.id`
 * que aparece em N dias, mescla os campos (não-vazio ganha) e reescreve em
 * todas as cópias. Garante consistência visual entre dias e prepara o
 * terreno pra um eventual refactor pra entidade única.
 *
 * @returns {{ days: Array, changed: boolean }} `days` é o array novo (cópia
 *   rasa, nunca muta input); `changed` indica se algum dia foi reescrito.
 */
export function healDuplicateRoutes(timelineDays) {
  if (!Array.isArray(timelineDays) || timelineDays.length === 0) {
    return { days: timelineDays || [], changed: false };
  }

  // 1) Pra cada id, acumula a versão "mais completa" via merge.
  //    Estratégia: pra cada campo, mantém o valor existente se já é
  //    "preenchido"; senão, copia do novo. Considera "vazio": undefined,
  //    null, '', []  — assim cópias com listas vazias não sobrescrevem
  //    listas preenchidas.
  const merged = new Map();
  for (const d of timelineDays) {
    for (const r of d?.routes || []) {
      if (!r?.id || typeof r !== 'object') continue;
      const existing = merged.get(r.id);
      merged.set(r.id, existing ? mergeRoutePair(existing, r) : { ...r });
    }
  }

  // 2) Reaplica a versão mergeada em cada cópia. Detecta se mudou alguma
  //    coisa pro caller decidir se persiste.
  let changed = false;
  const newDays = timelineDays.map((d) => {
    const routes = d?.routes;
    if (!Array.isArray(routes) || routes.length === 0) return d;
    let dayChanged = false;
    const newRoutes = routes.map((r) => {
      if (!r?.id) return r;
      const m = merged.get(r.id);
      if (!m) return r;
      // Diff field-by-field (defensive — JSON.stringify deep compare).
      if (JSON.stringify(m) === JSON.stringify(r)) return r;
      dayChanged = true;
      return { ...m };
    });
    if (!dayChanged) return d;
    changed = true;
    return { ...d, routes: newRoutes };
  });

  return { days: newDays, changed };
}

/**
 * Merge de 2 cópias da mesma rota. Pra cada campo do B, sobrescreve A só
 * se A está "vazio" (undefined/null/''/[]). Preserva o id (sempre presente).
 * Idempotente: mergeRoutePair(merge(a,b), b) === merge(a,b).
 */
function mergeRoutePair(a, b) {
  const out = { ...a };
  for (const [k, v] of Object.entries(b)) {
    if (v === undefined || v === null) continue;
    if (typeof v === 'string' && v === '') continue;
    if (Array.isArray(v) && v.length === 0) continue;
    const cur = out[k];
    const isEmpty = cur === undefined || cur === null
      || (typeof cur === 'string' && cur === '')
      || (Array.isArray(cur) && cur.length === 0);
    if (isEmpty) out[k] = v;
  }
  return out;
}

/**
 * Deriva hotéis canônicos a partir do shape legado (days[].hotel replicado
 * em N dias consecutivos). Para cada GRUPO de dias consecutivos com mesmo
 * name+address, cria 1 item com:
 *   - checkin  = data do primeiro dia do grupo
 *   - checkout = data do dia DEPOIS do último (paridade com legado: último
 *                dia listado tem o hotel, mas no checkout dia o hotel sai).
 *     Quando o grupo termina no último dia da viagem, checkout = checkin
 *     (mesma data) — sem dia "post-checkout".
 *
 * Hotéis NÃO consecutivos com mesmo nome ficam como stays SEPARADOS
 * (preferir granularidade ao invés de fundir — segurança contra dados
 * estranhos).
 *
 * Cada item recebe id estável `h_<random>` na primeira derivação.
 */
export function deriveHotelsFromTimelineDays(timelineDays) {
  const out = [];
  if (!Array.isArray(timelineDays) || timelineDays.length === 0) return out;

  let i = 0;
  while (i < timelineDays.length) {
    const h = timelineDays[i]?.hotel;
    const key = hotelDedupKey(h);
    if (!key) { i++; continue; }
    let j = i;
    while (j + 1 < timelineDays.length) {
      const nextH = timelineDays[j + 1]?.hotel;
      if (hotelDedupKey(nextH) === key) j++;
      else break;
    }
    const checkin = timelineDays[i].date;
    const checkout = (j + 1 < timelineDays.length)
      ? timelineDays[j + 1].date
      : checkin;
    out.push({
      id: h.id || newHotelId(),
      ...h,
      checkin,
      checkout,
      ts: h.ts || Date.now(),
    });
    i = j + 1;
  }
  return out;
}

function hotelDedupKey(h) {
  if (!h?.name) return '';
  const name = String(h.name).trim().toLowerCase();
  const addr = String(h.address || '').trim().toLowerCase();
  return `${name}::${addr}`;
}

function newHotelId() {
  return 'h_' + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-3);
}

/**
 * Lookup do hotel canônico vigente num dado dia. Regra: day.date >= checkin
 * AND day.date < checkout (paridade com legado — no dia de checkout o hotel
 * NÃO aparece). Quando checkin === checkout (stay de 1 noite no fim da
 * viagem), inclui o dia de checkin.
 *
 * Devolve o hotel ou null. Lookup linear (poucos hotéis por trip).
 */
export function lookupHotelForDay(hotelsItems, dayDate) {
  if (!Array.isArray(hotelsItems) || !dayDate) return null;
  for (const h of hotelsItems) {
    if (!h?.checkin) continue;
    if (h.checkin === dayDate) return h;
    // Caso 1-day stay (checkin === checkout): só checkin acima já cobriu.
    if (h.checkout && h.checkin !== h.checkout) {
      if (dateBetween(dayDate, h.checkin, h.checkout)) return h;
    }
  }
  return null;
}

// Compara DD/MM strings. Retorna true se `d` está no range [start, end)
// (start inclusivo, end exclusivo). Não considera ano — assume todas as
// datas dentro do mesmo ano de viagem (limitação herdada do shape DD/MM).
function dateBetween(d, start, end) {
  const cmp = (a, b) => {
    const [ad, am] = String(a).split('/').map(Number);
    const [bd, bm] = String(b).split('/').map(Number);
    if (am !== bm) return am - bm;
    return ad - bd;
  };
  return cmp(d, start) >= 0 && cmp(d, end) < 0;
}

/**
 * Re-sincroniza o timeline.days[].hotel com o canônico (após mutate em
 * hotels). Reescreve o blob timeline preservando todos os outros campos
 * dos days. Idempotente.
 */
async function syncTimelineHotelsFromCanonical(tripId, hotelsItems) {
  const timeline = await cloudLoad(tripId, 'timeline');
  const days = Array.isArray(timeline.days) ? timeline.days : [];
  const next = days.map((d) => ({
    ...d,
    hotel: (() => {
      const h = lookupHotelForDay(hotelsItems, d.date);
      return h ? { ...h } : null;
    })(),
  }));
  await cloudSave(tripId, 'timeline', { ...timeline, days: next, _ts: Date.now() });
}

/**
 * Gera dias com `date` real (DD/MM) e `title` "Dia N" baseado em
 * trip.startDate + trip.endDate. Aceita ambos os formatos que a base tem:
 *
 *   - ISO "YYYY-MM-DD"  → vindo do newTripModal (flatpickr ISO)
 *   - DD/MM             → vindo do legacy home.js (já normalizado)
 *
 * Year vem do ISO ou de trip.year (fallback: ano atual). Devolve [] se as
 * datas não fizerem sentido (parse falhou, end < start, ou span absurdo).
 *
 * NÃO persiste — caller decide se grava.
 */
export function generateBlankDaysFromTripDates(trip) {
  if (!trip?.startDate || !trip?.endDate) return [];

  // Detecta formato e normaliza pra Date(). Year é extraído quando ISO.
  // Se um lado é ISO e outro DD/MM (mistura inesperada), o year do ISO
  // ganha — DD/MM cai pro mesmo year se possível.
  const parsed = normalizeTripDateRange(trip);
  if (!parsed) return [];
  const { startDt, endDt } = parsed;
  if (endDt < startDt) return [];

  const totalDays = Math.round((endDt.getTime() - startDt.getTime()) / 86400000) + 1;
  if (totalDays <= 0 || totalDays > 365) return [];

  const days = [];
  for (let i = 0; i < totalDays; i++) {
    const d = new Date(startDt.getTime() + i * 86400000);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    days.push({
      date: `${dd}/${mm}`,
      icon: '📍',
      title: `Dia ${i + 1}`,
      details: '',
      color: trip.coverColor || '',
      cities: [],
      routes: [],
      hotel: null,
    });
  }
  return days;
}

// Normaliza um par startDate/endDate (DD/MM ou ISO) pra { startDt, endDt }
// como objetos Date. Retorna null se algum não parseou.
function normalizeTripDateRange(trip) {
  const explicitYear = trip.year || null;
  const isoMatch = (s) => String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  const ddmmMatch = (s) => String(s || '').match(/^(\d{1,2})\/(\d{1,2})$/);

  const startIso = isoMatch(trip.startDate);
  const endIso   = isoMatch(trip.endDate);
  const startDdmm = ddmmMatch(trip.startDate);
  const endDdmm   = ddmmMatch(trip.endDate);

  // Year sources, em ordem de prioridade: ISO da start, ISO da end,
  // trip.year explícito, ano atual.
  const year = startIso ? parseInt(startIso[1], 10)
              : endIso  ? parseInt(endIso[1], 10)
              : explicitYear || new Date().getFullYear();

  const buildIso = (m) => new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
  const buildDdmm = (m) => new Date(year, parseInt(m[2], 10) - 1, parseInt(m[1], 10));

  let startDt = startIso ? buildIso(startIso) : startDdmm ? buildDdmm(startDdmm) : null;
  let endDt   = endIso   ? buildIso(endIso)   : endDdmm   ? buildDdmm(endDdmm)   : null;

  if (!startDt || !endDt || isNaN(startDt) || isNaN(endDt)) return null;
  return { startDt, endDt };
}

/**
 * Detecta dias "sintéticos" — sem data real (DD/MM), criados pra viagens que
 * ainda não têm startDate/endDate. Convenção: `date === "d<N>"` (d1, d2…).
 * UI usa isso pra renderizar "Dia N" em vez de tentar parsear como calendário.
 */
export function isSyntheticDate(date) {
  return typeof date === 'string' && /^d\d+$/.test(date);
}

/**
 * Adiciona um dia ao final da timeline da trip. Comportamento:
 *
 *   - Se `opts.date` ("DD/MM") for passado, usa ele direto (caller já
 *     pediu pro user via date picker). É o caminho preferido.
 *   - Se `opts.synthetic === true`, força um dia sem data (usa key "dN").
 *     Usado quando o user clica "Sem data" no modal.
 *   - Sem opts: fallback automático — última data + 1, ou trip.startDate,
 *     ou sintético. Mantém retrocompat pra callers antigos.
 *
 * Valida unicidade do `date` (não permite duplicar real ou sintético).
 * Persiste e devolve o day criado.
 */
export async function addBlankDay(tripId, trip = null, opts = {}) {
  if (!tripId) throw new Error('addBlankDay: tripId obrigatório');
  const timeline = await cloudLoad(tripId, 'timeline');
  const days = Array.isArray(timeline.days) ? [...timeline.days] : [];
  const taken = new Set(days.map((d) => d.date));

  let date;
  // 1. Caller passou a data explicitamente (ex: vindo de date picker).
  if (typeof opts.date === 'string' && /^(\d{1,2})\/(\d{1,2})$/.test(opts.date)) {
    if (taken.has(opts.date)) {
      throw new Error(`addBlankDay: já existe um dia em ${opts.date}`);
    }
    date = opts.date;
  }
  // 2. Caller pediu sintético explícito (escape "Sem data").
  else if (opts.synthetic) {
    date = nextSyntheticKey(taken, days.length + 1);
  }
  // 3. Fallback automático (compat).
  else {
    const last = days[days.length - 1];
    if (last && /^(\d{1,2})\/(\d{1,2})$/.test(last.date)) {
      const year = trip?.year || new Date().getFullYear();
      const m = last.date.match(/^(\d{1,2})\/(\d{1,2})$/);
      const next = new Date(year, parseInt(m[2], 10) - 1, parseInt(m[1], 10) + 1);
      const dd = String(next.getDate()).padStart(2, '0');
      const mm = String(next.getMonth() + 1).padStart(2, '0');
      const candidate = `${dd}/${mm}`;
      date = taken.has(candidate) ? nextSyntheticKey(taken, days.length + 1) : candidate;
    } else if (!last && trip?.startDate && /^\d{1,2}\/\d{1,2}$/.test(trip.startDate)) {
      date = taken.has(trip.startDate) ? nextSyntheticKey(taken, days.length + 1) : trip.startDate;
    } else {
      date = nextSyntheticKey(taken, days.length + 1);
    }
  }

  const newDay = {
    date,
    icon: '📍',
    title: `Dia ${days.length + 1}`,
    details: '',
    color: trip?.coverColor || '',
    cities: [],
    routes: [],
    hotel: null,
  };
  days.push(newDay);
  // Mantém ordenação cronológica pra dias com datas reais — sintéticos vão
  // pro fim (ordenam pelo número). Isso evita que adicionar um dia "no meio"
  // (data passada) fique fora de lugar no strip.
  days.sort(compareDays);
  await cloudSave(tripId, 'timeline', { ...timeline, days, _ts: Date.now() });
  return newDay;
}

function nextSyntheticKey(taken, startN) {
  let n = startN;
  while (taken.has(`d${n}`)) n++;
  return `d${n}`;
}

// Ordena dias: reais (DD/MM) por mês/dia ascendente; sintéticos (dN) pelo
// número, sempre depois dos reais. Estável pra dias com mesma data (não
// devia acontecer mas defensivo).
function compareDays(a, b) {
  const aSyn = isSyntheticDate(a.date);
  const bSyn = isSyntheticDate(b.date);
  if (aSyn !== bSyn) return aSyn ? 1 : -1;
  if (aSyn && bSyn) {
    return parseInt(a.date.slice(1), 10) - parseInt(b.date.slice(1), 10);
  }
  const [ad, am] = a.date.split('/').map(Number);
  const [bd, bm] = b.date.split('/').map(Number);
  if (am !== bm) return am - bm;
  return ad - bd;
}

/**
 * Aplica overrides do itinerary nos days do timeline.
 * - dayTitle/dayIcon/color do override sobrescrevem se presentes
 * - items[] vem do override (legacy não tinha items no timeline)
 */
function mergeDays(timelineDays, itineraryOverrides) {
  return timelineDays.map((day) => {
    const ov = itineraryOverrides[day.date] || {};
    return {
      // Defaults do timeline
      date: day.date,
      icon: ov.dayIcon || day.icon || '📍',
      title: ov.dayTitle || day.title || '',
      details: day.details || '',
      color: ov.color || day.color || '',
      hotel: day.hotel || null,
      routes: day.routes || [],
      cities: day.cities || [],
      // Capa custom — persistida no timeline blob via setDayCover.
      // Shape: { source, url, bareKey?, photographer?, photographerUrl?, ts }.
      cover: day.cover || null,
      // Items — só do itinerary override (timeline não tem isso)
      items: Array.isArray(ov.items) ? ov.items : [],
    };
  });
}

/**
 * Atualiza o override de UM dia (title, icon, color) no itinerary blob.
 * - Lê o blob inteiro, modifica overrides[date], grava de volta.
 * - Não toca em items[] (eles ficam preservados se já existiam).
 *
 * Use quando user editar título/ícone na capa do dia.
 */
export async function updateDayMeta(tripId, dateStr, patch) {
  if (!dateStr) throw new Error('updateDayMeta: dateStr obrigatório');
  const itinerary = await cloudLoad(tripId, 'itinerary');
  const overrides = { ...(itinerary.overrides || {}) };
  const current = overrides[dateStr] || {};
  overrides[dateStr] = {
    ...current,
    ...(patch.title !== undefined ? { dayTitle: patch.title } : {}),
    ...(patch.icon  !== undefined ? { dayIcon:  patch.icon }  : {}),
    ...(patch.color !== undefined ? { color:    patch.color } : {}),
  };
  await cloudSave(tripId, 'itinerary', { ...itinerary, overrides, _ts: Date.now() });
}

/**
 * Salva a capa custom de UM dia no TIMELINE blob (não itinerary — capa vive
 * junto do day, igual hotel/routes/cities).
 *
 * Shape esperado pra `cover`:
 *   {
 *     source: 'unsplash' | 'upload',
 *     url: string,                   // URL completa pra renderizar como bg
 *     bareKey?: string,              // só source==='upload' — R2 bareFilename
 *     photographer?: string,         // só source==='unsplash'
 *     photographerUrl?: string,      // só source==='unsplash' (com UTM)
 *     ts: number,                    // Date.now() do save
 *   }
 *
 * Passe `null` pra remover (volta pro auto-load via getTripImage). Persistência
 * fica no blob da viagem, então sobrevive entre devices/sessões — é a "cache"
 * que o user vê depois de configurar a capa.
 */
export async function setDayCover(tripId, dateStr, cover) {
  if (!dateStr) throw new Error('setDayCover: dateStr obrigatório');
  const timeline = await cloudLoad(tripId, 'timeline');
  const days = (timeline.days || []).map((d) => {
    if (d.date !== dateStr) return d;
    return { ...d, cover: cover || null };
  });
  await cloudSave(tripId, 'timeline', { ...timeline, days, _ts: Date.now() });
}

/**
 * Salva a capa custom da VIAGEM inteira em trip.cover (não no timeline).
 * Análogo a setDayCover mas atuando no doc da trip via upsertTrip.
 *
 * Shape esperado pra `cover`:
 *   {
 *     source: 'unsplash' | 'upload',
 *     url: string,
 *     bareKey?: string,
 *     photographer?: string,
 *     photographerUrl?: string,
 *     ts: number,
 *   }
 *
 * Passe `null` pra remover (caller volta pro auto-load via getTripImage).
 * `currentTrip` é OBRIGATÓRIO porque upsertTrip substitui o doc inteiro —
 * caller passa a trip do STATE pra preservar todos os outros campos.
 *
 * Retorna o trip atualizado (resultado do upsertTrip).
 */
export async function setTripCover(tripId, cover, currentTrip) {
  if (!tripId) throw new Error('setTripCover: tripId obrigatório');
  if (!currentTrip || currentTrip.id !== tripId) {
    throw new Error('setTripCover: currentTrip obrigatório (precisa do doc inteiro pra upsert)');
  }
  // Lazy-import do firestore pra evitar ciclo de dependências (roteiro/api
  // é importado em vários lugares e core/firestore puxa firebase inteiro).
  const { upsertTrip } = await import('../../core/firestore.js');
  return upsertTrip({ ...currentTrip, cover: cover || null });
}

/**
 * Atualiza items[] de um dia (substitui o array inteiro).
 * Items: [{ id, time: "HH:MM", emoji, title, details }]
 */
export async function setDayItems(tripId, dateStr, items) {
  if (!dateStr) throw new Error('setDayItems: dateStr obrigatório');
  const itinerary = await cloudLoad(tripId, 'itinerary');
  const overrides = { ...(itinerary.overrides || {}) };
  const current = overrides[dateStr] || {};
  overrides[dateStr] = { ...current, items: Array.isArray(items) ? items : [] };
  await cloudSave(tripId, 'itinerary', { ...itinerary, overrides, _ts: Date.now() });
}

// ---------- Hotel CRUD (canônico: blob `hotels._legacy`) ----------
//
// Toda mutation passa pelo canônico. setDayHotel mantém a assinatura legada
// (caller `handleHotelEdit` passa o hotel inteiro do hotelModal + dateStr do
// dia onde o user abriu o modal), mas internamente:
//
//   1. Se hotel.id existe no canônico → UPDATE por id (não importa em qual
//      dia o user abriu o modal; checkin/checkout do hotel comandam).
//   2. Se hotel não tem id (criação) → INSERT com id novo. Default de
//      checkin/checkout: o dateStr passado (single-day) se hotel não trouxer
//      explícitos.
//   3. Se hotel.id é desconhecido (id legado vindo de outra trip ou drift)
//      → trata como insert preservando o id.
//
// Passe `hotel === null` pra remover do dia. NOTA: o legado removia só a
// noite daquele dia; aqui, removemos o stay INTEIRO se o canônico só tem
// uma entrada cobrindo esse dia. Pra remover stays com granularidade, use
// removeHotelById diretamente.
//
// Após a mutation no canônico, re-deriva o timeline pra manter a view em
// sincronia (renderers e reorder ainda consomem day.hotel).
//
// Retorna o hotel salvo (com id final), ou null em delete.

export async function setDayHotel(tripId, dateStr, hotel) {
  if (!dateStr) throw new Error('setDayHotel: dateStr obrigatório');
  const hotelsBlob = await cloudLoad(tripId, 'hotels');
  let items = Array.isArray(hotelsBlob.items) ? [...hotelsBlob.items] : [];

  if (!hotel) {
    // Delete: remove o stay que cobre o dia.
    const existing = lookupHotelForDay(items, dateStr);
    if (existing) {
      items = items.filter((h) => h.id !== existing.id);
    }
    await cloudSave(tripId, 'hotels', { ...hotelsBlob, items, _ts: Date.now() });
    await syncTimelineHotelsFromCanonical(tripId, items);
    return null;
  }

  // Update vs insert
  const hasId = !!hotel.id;
  const existingIdx = hasId ? items.findIndex((h) => h.id === hotel.id) : -1;
  // Defaults de range: usa o que veio no hotel, ou (na ausência) o dateStr
  // passado pelo caller — equivale ao comportamento legado "1 noite no dia
  // de hoje" quando user adicionava sem mexer nas datas.
  const checkin = hotel.checkin || dateStr;
  const checkout = hotel.checkout || dateStr;
  const next = {
    ...hotel,
    id: hotel.id || newHotelId(),
    checkin,
    checkout,
    ts: hotel.ts || Date.now(),
  };

  if (existingIdx >= 0) {
    items[existingIdx] = { ...items[existingIdx], ...next };
  } else {
    items.push(next);
  }

  await cloudSave(tripId, 'hotels', { ...hotelsBlob, items, _ts: Date.now() });
  await syncTimelineHotelsFromCanonical(tripId, items);
  return next;
}

/** Lookup direto por id (usado por handlers que precisam do hotel completo). */
export async function getHotelById(tripId, hotelId) {
  if (!hotelId) return null;
  const hotelsBlob = await cloudLoad(tripId, 'hotels').catch(() => ({}));
  const items = Array.isArray(hotelsBlob.items) ? hotelsBlob.items : [];
  return items.find((h) => h.id === hotelId) || null;
}

/** Remove hotel canônico por id. Re-deriva timeline. */
export async function removeHotelById(tripId, hotelId) {
  if (!hotelId) return;
  const hotelsBlob = await cloudLoad(tripId, 'hotels');
  const items = (hotelsBlob.items || []).filter((h) => h.id !== hotelId);
  await cloudSave(tripId, 'hotels', { ...hotelsBlob, items, _ts: Date.now() });
  await syncTimelineHotelsFromCanonical(tripId, items);
}

// ---------- Hotel → itens de itinerário (check-in / check-out) ----------
//
// Garante 2 itens vinculados ao hotel: 1 no dia de check-in (chegada) e 1
// no dia de check-out (saída). Atualiza in-place se já existirem, cria se
// faltar. Itens são identificados por `linkedHotelId === hotel.id`.
//
// É a versão compartilhada da lógica que /roteiro/page.js já fazia inline —
// extraída pra cá pra que a tela do mapa (map/page.js) consiga criar os
// mesmos itens ao adicionar um hotel pela sugestão.
//
// `days` é o snapshot atual dos dias (com `.items` mesclado) — usado só pra
// ler os itens existentes de cada dia. O caller deve recarregar os dias
// depois (loadRoteiro) pra ver o resultado.
//
// Defaults de horário: check-in 15:00, check-out 12:00. Quando checkin ===
// checkout (stay de 1 noite) cria só o item de check-in.
export async function syncHotelLinkedItems(tripId, days, hotel) {
  if (!tripId || !hotel?.id || !hotel.checkin) return;
  const checkinDate = hotel.checkin;
  const checkoutDate = hotel.checkout && hotel.checkout !== hotel.checkin
    ? hotel.checkout : null;

  // Limpa itens órfãos: se o range do hotel mudou, itens de check-in/out
  // criados em dias antigos ficam soltos. Varre todos os dias e remove
  // itens com linkedHotelId === hotel.id fora dos dias novos.
  const keepDates = new Set([checkinDate]);
  if (checkoutDate) keepDates.add(checkoutDate);
  for (const d of days || []) {
    if (!Array.isArray(d?.items) || d.items.length === 0) continue;
    if (keepDates.has(d.date)) continue;
    const filtered = d.items.filter((it) => it?.linkedHotelId !== hotel.id);
    if (filtered.length !== d.items.length) {
      await setDayItems(tripId, d.date, filtered);
    }
  }

  await _upsertHotelLinkedItem(tripId, days, checkinDate, hotel.id, {
    type: 'hotel',
    title: `Check-in: ${hotel.name || 'hotel'}`,
    time: hotel.checkinTime || '15:00',
  });
  if (checkoutDate) {
    await _upsertHotelLinkedItem(tripId, days, checkoutDate, hotel.id, {
      type: 'hotel',
      title: `Check-out: ${hotel.name || 'hotel'}`,
      time: hotel.checkoutTime || '12:00',
    });
  }
}

// Upsert idempotente de UM item vinculado a um hotel num dia. Identifica o
// item existente por `linkedHotelId === hotelId`. Re-ordena por horário.
async function _upsertHotelLinkedItem(tripId, days, dayDate, hotelId, patch) {
  const day = (days || []).find((d) => d?.date === dayDate);
  if (!day) return;   // dia fora do range da viagem — ignora
  const items = Array.isArray(day.items) ? [...day.items] : [];
  const idx = items.findIndex((it) => it?.linkedHotelId === hotelId);
  if (idx >= 0) {
    items[idx] = {
      ...items[idx],
      title: patch.title,
      time: patch.time,
      type: patch.type,
      linkedHotelId: hotelId,
    };
  } else {
    items.push({
      id: 'it_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36).slice(-3),
      type: patch.type,
      title: patch.title,
      time: patch.time,
      linkedHotelId: hotelId,
    });
  }
  items.sort((a, b) => (a?.time || '99:99').localeCompare(b?.time || '99:99'));
  await setDayItems(tripId, dayDate, items);
}

// ---------- Route CRUD ----------

/**
 * Adiciona um route ao day.routes[]. `route` deve ter id próprio (gerado
 * pelo caller). Retorna o array atualizado pra o caller poder ordenar/usar.
 *
 * Route shape: {
 *   id, type: 'air'|'train'|'car'|'bus'|'boat'|'cable'|'walk',
 *   title?, from, to, fromLat?, fromLng?, toLat?, toLng?,
 *   departureDate: "DD/MM", departureTime: "HH:MM",
 *   arrivalDate: "DD/MM", arrivalTime: "HH:MM",
 *   price?, code?, status: 'a-reservar'|'reservado',
 *   bookingUrl?, notes?,
 *   // Voos (preenchidos na 6.1.j):
 *   airlineIata?, flightNumber?, flightStatusCacheKey?,
 *   expenseId?
 * }
 */
export async function addDayRoute(tripId, dateStr, route) {
  if (!dateStr) throw new Error('addDayRoute: dateStr obrigatório');
  if (!route?.id) throw new Error('addDayRoute: route.id obrigatório');
  const timeline = await cloudLoad(tripId, 'timeline');
  const days = (timeline.days || []).map((d) => {
    if (d.date !== dateStr) return d;
    return { ...d, routes: [...(d.routes || []), route] };
  });
  await cloudSave(tripId, 'timeline', { ...timeline, days, _ts: Date.now() });
  notifyNotifChanged();
}

/**
 * Atualiza um route por id (merge dos campos passados).
 *
 * Propagação multi-day: rotas com departureDate ≠ arrivalDate são REPLICADAS
 * em N days[i].routes. Aqui, varremos TODOS os days e aplicamos o patch a
 * QUALQUER ocorrência da mesma routeId — assim editar status/title/preço em
 * uma cópia propaga pra todas. Single-day routes só existem num day e o
 * comportamento é equivalente ao legado.
 */
export async function updateDayRoute(tripId, dateStr, routeId, patch) {
  if (!dateStr || !routeId) throw new Error('updateDayRoute: dateStr e routeId obrigatórios');
  const timeline = await cloudLoad(tripId, 'timeline');
  const days = (timeline.days || []).map((d) => {
    const routes = (d.routes || []).map((r) => (r?.id === routeId ? { ...r, ...patch } : r));
    return { ...d, routes };
  });
  await cloudSave(tripId, 'timeline', { ...timeline, days, _ts: Date.now() });
  notifyNotifChanged();
}

/**
 * Remove um route por id. Propaga em TODOS os days (cobre multi-day onde a
 * rota está replicada). Single-day cai no caso normal — só o day em
 * questão tem a route.
 */
export async function removeDayRoute(tripId, dateStr, routeId) {
  if (!dateStr || !routeId) throw new Error('removeDayRoute: dateStr e routeId obrigatórios');
  const timeline = await cloudLoad(tripId, 'timeline');
  const days = (timeline.days || []).map((d) => {
    const routes = (d.routes || []).filter((r) => r?.id !== routeId);
    return { ...d, routes };
  });
  await cloudSave(tripId, 'timeline', { ...timeline, days, _ts: Date.now() });
  notifyNotifChanged();
}

// Avisa a central de notificações (se montada) pra recalcular/reagendar.
function notifyNotifChanged() {
  try { window.mpNotifRefresh?.(); } catch { /* noop */ }
}

/**
 * Lookup direto de uma route por id em qualquer dia. Útil pra resolver
 * `linkedRouteId` de items sem precisar do dayDate.
 */
export function findRouteById(days, routeId) {
  if (!routeId || !Array.isArray(days)) return null;
  for (const d of days) {
    const r = (d?.routes || []).find((rr) => rr?.id === routeId);
    if (r) return r;
  }
  return null;
}

// ---------- Reorder de dias (drag-and-drop com remap de datas) ----------

/**
 * Move o bloco days[blockFrom..blockTo] pra targetIdx. Propaga o remap
 * de datas em itinerary (keys), expenses (keys + tx.expenseId.date) e
 * balance.transactions (tx.reference.day).
 *
 * Port (sem `window.*`) de `_reorderTimelineBlock` em routes/utils.js.
 *
 * Para UX otimista (update imediato + save em bg), prefira chamar
 * `computeReorderedDays` (em dayBlocks.js) + `persistDayReorder` aqui.
 *
 * @param {string} tripId
 * @param {Array} days        Lista atual de days (vem do mapa/roteiro STATE).
 * @param {number} blockFrom  Início do bloco a mover (inclusivo).
 * @param {number} blockTo    Fim do bloco (inclusivo).
 * @param {number} targetIdx  Posição alvo (caller já validou overlap).
 * @param {object} trip       Necessário pra inferir year das datas.
 * @returns {Promise<Array>}  newDays, já persistido.
 */
export async function reorderDays(tripId, days, blockFrom, blockTo, targetIdx, trip = null) {
  const { computeReorderedDays, guessTripYear } = await import('./dayBlocks.js');

  if (!Array.isArray(days) || days.length === 0) return days;
  if (blockFrom < 0 || blockTo >= days.length || blockFrom > blockTo) return days;
  if (targetIdx >= blockFrom && targetIdx <= blockTo) return days; // no-op

  const year = guessTripYear(trip);
  const { newDays, dateRemap } = computeReorderedDays(
    days, blockFrom, blockTo, targetIdx, year
  );
  await persistDayReorder(tripId, newDays, dateRemap);
  return newDays;
}

/**
 * Persiste um reorder já calculado. Use junto com `computeReorderedDays`
 * (em dayBlocks.js) quando precisar de update otimista: o caller atualiza
 * o STATE com newDays SYNC, re-renderiza, e dispara `persistDayReorder`
 * em background.
 *
 * Salva timeline + itinerary + expenses + meta em paralelo. Falhas
 * individuais em itinerary/expenses/meta são logadas mas não propagam
 * (timeline é prioritário — caller pode reverter STATE em falha do save
 * de timeline).
 *
 * @param {string} tripId
 * @param {Array} newDays     Resultado de computeReorderedDays.
 * @param {object} dateRemap  { oldDdmm: newDdmm } — keys que mudaram.
 * @returns {Promise<void>}   Rejeita se save de timeline falhar.
 */
export async function persistDayReorder(tripId, newDays, dateRemap) {
  if (!Array.isArray(newDays)) throw new Error('persistDayReorder: newDays inválido');
  const hasRemap = dateRemap && Object.keys(dateRemap).length > 0;

  // Lê blobs co-relatos só se houve remap real (otimização).
  const [timelineBlob, itineraryBlob, expensesBlob, balanceBlob] = await Promise.all([
    cloudLoad(tripId, 'timeline').catch(() => ({})),
    hasRemap ? cloudLoad(tripId, 'itinerary').catch(() => ({})) : Promise.resolve(null),
    hasRemap ? cloudLoad(tripId, 'expenses').catch(() => ({}))  : Promise.resolve(null),
    hasRemap ? cloudLoad(tripId, 'balance').catch(() => ({}))   : Promise.resolve(null),
  ]);

  function remapKeys(obj) {
    if (!obj || typeof obj !== 'object') return obj;
    const out = {};
    for (const [k, v] of Object.entries(obj)) {
      out[dateRemap[k] || k] = v;
    }
    return out;
  }

  const ts = Date.now();
  // Save timeline (síncrono / awaitado — caller depende dele pra rollback)
  await cloudSave(tripId, 'timeline', { ...timelineBlob, days: newDays, _ts: ts });

  if (!hasRemap) return;

  const nextItineraryOverrides = remapKeys(itineraryBlob?.overrides || {});
  const nextExpensesOverrides = remapKeys(expensesBlob?.overrides || {});
  for (const [k, list] of Object.entries(nextExpensesOverrides)) {
    if (!Array.isArray(list)) continue;
    nextExpensesOverrides[k] = list.map((exp) => {
      if (!exp || typeof exp !== 'object') return exp;
      if (exp.date && dateRemap[exp.date]) return { ...exp, date: dateRemap[exp.date] };
      return exp;
    });
  }
  const nextOtherExpenses = (expensesBlob?.otherExpenses || []).map((exp) => {
    if (!exp || typeof exp !== 'object') return exp;
    if (exp.date && dateRemap[exp.date]) return { ...exp, date: dateRemap[exp.date] };
    return exp;
  });

  // Transactions vivem em cloudLoad('balance').transactions (não 'meta').
  const txs = Array.isArray(balanceBlob?.transactions) ? balanceBlob.transactions : [];
  const nextTxs = txs.map((tx) => {
    if (!tx?.reference?.day || !dateRemap[tx.reference.day]) return tx;
    return { ...tx, reference: { ...tx.reference, day: dateRemap[tx.reference.day] } };
  });

  await Promise.all([
    cloudSave(tripId, 'itinerary', {
      ...itineraryBlob, overrides: nextItineraryOverrides, _ts: ts,
    }).catch((err) => console.warn('[persistDayReorder] itinerary failed:', err)),
    cloudSave(tripId, 'expenses', {
      ...expensesBlob,
      overrides: nextExpensesOverrides,
      otherExpenses: nextOtherExpenses,
      _ts: ts,
    }).catch((err) => console.warn('[persistDayReorder] expenses failed:', err)),
    cloudSave(tripId, 'balance', {
      ...balanceBlob,
      transactions: nextTxs,
      _ts: ts,
    }).catch((err) => console.warn('[persistDayReorder] balance failed:', err)),
  ]);
}

// ---------- Helpers de conversão de data ----------

/** "12/04" + 2026 → "2026-04-12" (formato `<input type=date>`). Null safe. */
export function dayDateToInputValue(ddmm, year) {
  const m = String(ddmm || '').match(/^(\d{1,2})\/(\d{1,2})$/);
  if (!m) return '';
  const dd = m[1].padStart(2, '0');
  const mm = m[2].padStart(2, '0');
  return `${year}-${mm}-${dd}`;
}

/** "2026-04-12" → "12/04". Null safe. */
export function inputValueToDayDate(yyyymmdd) {
  const m = String(yyyymmdd || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return '';
  return `${m[3]}/${m[2]}`;
}

/** "12/04" + "19:30" + 2026 → "2026-04-12T19:30" (datetime-local). */
export function dayDateTimeToInputValue(ddmm, hhmm, year) {
  const date = dayDateToInputValue(ddmm, year);
  if (!date) return '';
  const time = String(hhmm || '').match(/^(\d{1,2}):(\d{2})$/) ? hhmm.padStart(5, '0') : '00:00';
  return `${date}T${time}`;
}

/** "2026-04-12T19:30" → { date: "12/04", time: "19:30" }. */
export function inputValueToDayDateTime(local) {
  const m = String(local || '').match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
  if (!m) return { date: '', time: '' };
  return { date: `${m[3]}/${m[2]}`, time: `${m[4]}:${m[5]}` };
}

/**
 * Extrai cidades dos hotéis do dia — usado em UI (subtítulo nos cards/pílulas).
 * Pra QUERY de imagem use `buildDayImageQuery(day)` que combina hotel + rotas
 * + título limpo.
 *
 * Ex:
 *   "Pier Mauá, Centro, Rio de Janeiro" → ["Rio de Janeiro"]
 *   "Av. Atlântica 1000, Copacabana, Rio de Janeiro - RJ" → ["Rio de Janeiro"]
 *   sem hotel → []
 */
export function dayHotelCities(day) {
  const out = [];
  if (day?.hotel?.address) {
    const city = extractCityFromAddress(day.hotel.address);
    if (city) out.push(city);
  }
  return out;
}

/**
 * Constrói a query de busca de foto pro dia. Simplificada por design:
 *
 *   1. Se o dia tem hotel → retorna "cidade país" (extraídos do address)
 *   2. Senão → retorna o título do dia depois de limpar stop-words
 *
 * E ponto final. Routes, items, fallbacks complexos foram removidos —
 * geravam queries ruins. Se o usuário não setou hotel, o título é a
 * melhor pista que ele forneceu.
 *
 * Exemplos:
 *   hotel "5 Rue Hector Malot, 75012 Paris, França" → "Paris França"
 *   hotel "Via Merulana, ..., Roma, Lazio, Italy"   → "Roma Italy"
 *   hotel "..., Valdemoro, Espanha"                  → "Valdemoro Espanha"
 *   sem hotel + título "Partiu Vaticano"             → "Vaticano"
 *   sem hotel + título "Inicio Eurotrip"             → "" (tudo stop word)
 */
export function buildDayImageQuery(day) {
  let query = '';
  let source = '';

  // 1. Hotel — cidade + país
  if (day?.hotel?.address) {
    const { city, country } = extractCityAndCountry(day.hotel.address);
    if (city) {
      query = country ? `${city} ${country}` : city;
      source = 'hotel';
    }
  }

  // 2. Fallback: título limpo
  if (!query && day?.title) {
    const cleaned = stripTitleNoise(day.title);
    if (cleaned) {
      query = cleaned;
      source = 'title';
    }
  }

  console.log('[roteiro] query — day "%s" (source: %s) → "%s"',
    day?.title || day?.date || '(?)', source || 'none', query);

  return query;
}

// Extrai cidade + país de um address free-form (Google Places format).
//
// Estratégia (caminhando do fim pro início):
//   1. Limpa cada segmento (CEP prefix/suffix, estado BR " - XX")
//   2. Se o último segmento for país conhecido → guarda como `country`,
//      remove dos segmentos a vasculhar
//   3. Se o próximo segmento for região conhecida (Lazio, Toscana,
//      Andalucía...) → pula
//   4. Primeiro segmento que sobrar é a `city`
//
// Casos cobertos:
//   "Pier Mauá, Rio de Janeiro"               → { city: "Rio de Janeiro" }
//   "Alcalde..., 28342 Valdemoro, Espanha"    → { city: "Valdemoro", country: "Espanha" }
//   "Via Merulana, ..., Roma, Lazio, Italy"   → { city: "Roma", country: "Italy" }
//   "5 Rue Hector Malot, 75012 Paris, França" → { city: "Paris", country: "França" }
//   "Hotel Arlette, ..., Zürich, Suíça"       → { city: "Zürich", country: "Suíça" }
export function extractCityAndCountry(addr) {
  const parts = String(addr || '').split(',').map((s) => s.trim()).filter(Boolean);
  if (!parts.length) return { city: '', country: '' };

  const cleaned = parts.map((s) => s
    .replace(/^\s*\d{4,5}-?\d{0,3}\s+/, '')
    .replace(/\s*-\s*[A-Z]{2}\b.*$/, '')
    .replace(/\b\d{4,5}-?\d{0,3}\b/g, '')
    .trim()
  ).filter(Boolean);

  if (!cleaned.length) return { city: '', country: '' };

  // País: só se o último segmento for explicitamente reconhecido como país.
  let country = '';
  let segments = cleaned;
  const lastNorm = removeAccents(cleaned[cleaned.length - 1].toLowerCase());
  if (KNOWN_COUNTRIES.has(lastNorm)) {
    country = cleaned[cleaned.length - 1];
    segments = cleaned.slice(0, -1);
  }

  // Cidade: caminha de trás pra frente nos segments restantes, pulando regiões.
  let city = '';
  for (let i = segments.length - 1; i >= 0; i--) {
    const norm = removeAccents(segments[i].toLowerCase());
    if (KNOWN_REGIONS.has(norm)) continue;
    city = segments[i];
    break;
  }
  // Se sobrou nada (tudo era região), pega o último mesmo.
  if (!city && segments.length) city = segments[segments.length - 1];

  return { city, country };
}

// Mantém compatibilidade — `dayHotelCities` ainda usa, e algumas UIs talvez.
function extractCityFromAddress(addr) {
  return extractCityAndCountry(addr).city;
}

// Países (nome em PT/EN/nativo, accent-insensitive). Não exaustivo — só os
// que aparecem em viagens comuns. Adicione conforme necessário.
const KNOWN_COUNTRIES = new Set([
  // PT
  'brasil', 'espanha', 'italia', 'franca', 'portugal', 'suica',
  'alemanha', 'reino unido', 'inglaterra', 'estados unidos', 'canada',
  'japao', 'china', 'russia', 'holanda', 'paises baixos', 'belgica',
  'austria', 'grecia', 'turquia', 'egito', 'marrocos', 'india',
  'argentina', 'chile', 'mexico', 'peru', 'uruguai', 'colombia',
  'tailandia', 'vietna', 'indonesia', 'australia', 'nova zelandia',
  'irlanda', 'escocia', 'noruega', 'suecia', 'dinamarca', 'finlandia',
  'islandia', 'polonia', 'republica tcheca', 'hungria', 'romenia',
  'croacia', 'eslovenia', 'eslovaquia',
  // EN
  'brazil', 'spain', 'italy', 'france', 'switzerland', 'germany',
  'united kingdom', 'uk', 'usa', 'us', 'united states', 'japan',
  'netherlands', 'belgium', 'greece', 'turkey', 'egypt', 'morocco',
  'india', 'mexico', 'colombia', 'thailand', 'vietnam', 'indonesia',
  'australia', 'new zealand', 'ireland', 'scotland', 'norway', 'sweden',
  'denmark', 'finland', 'iceland', 'poland', 'czech republic', 'hungary',
  'romania', 'croatia', 'slovenia', 'slovakia',
  // Nativo
  'espana', 'deutschland', 'schweiz', 'osterreich', 'belgique',
  'nederland', 'magyarorszag',
]);

// Regiões/estados de países comuns que NÃO são cidades (pra pular no
// extractCityFromAddress). Cidade-regiões (Madrid, Berlin, Zürich) NÃO
// entram aqui — quando aparecem em addresses tipicamente são cidades.
const KNOWN_REGIONS = new Set([
  // Itália
  'lazio', 'lombardia', 'toscana', 'sicilia', 'veneto', 'piemonte',
  'campania', 'liguria', 'emilia-romagna', 'puglia', 'sardegna',
  'calabria', 'marche', 'umbria', 'abruzzo', 'molise', 'basilicata',
  'friuli-venezia giulia', 'trentino-alto adige',
  // Espanha (não inclui Madrid/Catalunya — usados como cidade em addresses)
  'andalucia', 'galicia', 'extremadura', 'asturias', 'cantabria',
  'navarra', 'la rioja', 'aragon', 'castilla y leon', 'castilla la mancha',
  'islas baleares', 'islas canarias',
  // França (Île-de-France contém Paris mas é região; lista pra Paris ganhar)
  'ile-de-france', 'provence', 'provence-alpes-cote d azur', 'normandie',
  'bretagne', 'occitanie', 'nouvelle-aquitaine', 'auvergne-rhone-alpes',
  'grand est', 'pays de la loire', 'centre-val de loire', 'bourgogne',
]);

// Stop-words PT/EN que aparecem em títulos de dia mas atrapalham a busca.
// Match exato da palavra (case-insensitive, accent-insensitive). Cobre:
//   - tempo: dia/hoje/manhã/tarde/noite
//   - movimento: partiu/chegada/embarque/saída/rumo/voltar
//   - estado: início/fim/começo/término/primeiro/último
//   - genéricos: viagem/eurotrip/tour/passeio (palavras que descrevem
//     a trip como um todo, não o dia específico — o título da trip já
//     contamina a busca)
//   - preposições/artigos PT + EN
const TITLE_STOP_WORDS = new Set([
  // tempo
  'dia', 'dias', 'hoje', 'manha', 'manhã', 'tarde', 'noite', 'amanha', 'amanhã',
  // movimento
  'partiu', 'partida', 'partindo', 'saída', 'saida', 'saindo',
  'chegada', 'chegou', 'chegamos', 'chegando',
  'volta', 'voltar', 'voltando', 'voltamos', 'retorno',
  'despedida', 'adeus', 'tchau',
  'embarque', 'embarcando', 'desembarque', 'desembarcando',
  'rumo', 'caminho', 'travessia', 'rota', 'correria',
  // estado/ordem
  'inicio', 'início', 'começo', 'comeco', 'fim', 'final', 'término', 'termino',
  'primeiro', 'segundo', 'terceiro', 'ultimo', 'último', 'last', 'first',
  // genéricos de viagem (não falam do dia, só do contexto)
  'viagem', 'viagens', 'tour', 'tours', 'passeio', 'passeios', 'trip', 'travel',
  'eurotrip', 'roadtrip', 'getaway', 'férias', 'ferias', 'vacation',
  // preposições/artigos PT
  'de', 'do', 'da', 'dos', 'das', 'em', 'no', 'na', 'nos', 'nas',
  'para', 'pra', 'pro', 'por', 'pelo', 'pela', 'a', 'o', 'e', 'ou',
  'com', 'sem', 'até', 'ate',
  // EN
  'the', 'and', 'in', 'on', 'at', 'to', 'from', 'of', 'a', 'an', 'with', 'by',
]);

function stripTitleNoise(title) {
  // Remove conteúdo entre parênteses primeiro (geralmente meta/anotação)
  const noParens = String(title || '').replace(/\([^)]*\)/g, ' ');
  const words = noParens
    .split(/\s+/)
    .map((w) => w.replace(/[^\p{L}\p{N}]/gu, ''))   // remove pontuação ao redor
    .filter((w) => w.length >= 2)
    .filter((w) => !/^\d+$/.test(w))                // descarta puro número
    .filter((w) => !TITLE_STOP_WORDS.has(w.toLowerCase()))
    .filter((w) => !TITLE_STOP_WORDS.has(removeAccents(w.toLowerCase())));
  return words.join(' ').trim();
}

function removeAccents(s) {
  return String(s).normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}
